import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isSupabaseConfigured, supabase } from "@/lib/supabaseClient";
import { toast } from "@/hooks/use-toast";

export const AuthModal: React.FC<{ open: boolean; onClose: () => void; onAuthSuccess?: () => void }> = ({ open, onClose, onAuthSuccess }) => {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const ensureSupabase = () => {
    if (!isSupabaseConfigured || !supabase) {
      toast({
        title: "Authentication unavailable",
        description: "Supabase is not configured for this deployment.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const validateCredentials = () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return false;
    }
    if (password.length < 8) {
      toast({ title: "Password too short", description: "Password must be at least 8 characters.", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSignUp = async () => {
    if (!ensureSupabase()) return;
    if (!validateCredentials()) return;
    if (password !== confirmPassword) {
      toast({ title: "Password mismatch", description: "Passwords do not match", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // pass name as user metadata so it's available on the user object
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: name } },
      });
      if (error) throw error;
      toast({ title: "Check your email", description: "A confirmation link has been sent (if required)." });
      onAuthSuccess?.();
      onClose();
    } catch (err: any) {
      toast({ title: "Sign up error", description: err.message ?? String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!ensureSupabase()) return;
    if (!validateCredentials()) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast({ title: "Signed in", description: "You are now signed in." });
      onAuthSuccess?.();
      onClose();
    } catch (err: any) {
      toast({ title: "Sign in error", description: err.message ?? String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: string) => {
    if (!ensureSupabase()) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({ provider: provider as any, options: { redirectTo: window.location.origin } });
      if (error) throw error;
      // For OAuth, Supabase will redirect the browser; no further action here.
    } catch (err: any) {
      toast({ title: "OAuth error", description: err.message ?? String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70">
      <Card className="p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`px-3 py-1 rounded ${mode === "login" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={`px-3 py-1 rounded ${mode === "signup" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              Sign Up
            </button>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <h3 className="mb-2 text-lg font-semibold">{mode === "login" ? "Sign in" : "Create account"}</h3>
        {!isSupabaseConfigured && (
          <div className="mb-3 text-sm text-destructive">
            Authentication is unavailable because Supabase environment variables are missing.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-sm text-muted-foreground">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>

          {mode === "signup" && (
            <div>
              <label className="text-sm text-muted-foreground">Full name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} type="text" />
            </div>
          )}

          <div>
            <label className="text-sm text-muted-foreground">Password</label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? "text" : "password"} />
          </div>

          {mode === "signup" && (
            <div>
              <label className="text-sm text-muted-foreground">Confirm password</label>
              <Input value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} type={showPassword ? "text" : "password"} />
            </div>
          )}

          <div className="flex items-center gap-3">
            <input id="show-pass" type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
            <label htmlFor="show-pass" className="text-sm text-muted-foreground">Show password</label>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => handleOAuth("google")}
              disabled={loading || !isSupabaseConfigured}
              className="flex items-center justify-center gap-2 bg-[#574F2A] text-white hover:bg-[#357ae8] border-transparent"
            >
              {/* Google G Logo */}
              <svg
                viewBox="0 0 24 24"
                width={18}
                height={18}
                className="h-4 w-4"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>
          </div>

          <div className="flex gap-2 mt-2">
            {mode === "login" ? (
              <Button onClick={handleSignIn} disabled={loading || !isSupabaseConfigured}>Log in</Button>
            ) : (
              <Button onClick={handleSignUp} disabled={loading || !isSupabaseConfigured}>Create account</Button>
            )}
            <Button variant="ghost" className="ml-auto" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AuthModal;
